(function initRequestedDateUtils(global) {
	const DEFAULT_TIMEZONE = "America/Chicago";
	const DEFAULT_CUTOFF_HOUR = 6;
	const timezoneConfig =
		global.REQUESTED_DATE_CONFIG?.timezone ||
		global.REQUESTED_DATE_TIMEZONE ||
		DEFAULT_TIMEZONE;

	const resolveTimeZone = (value) => {
		if (!value) return DEFAULT_TIMEZONE;
		const timezoneAliases = {
			"Central Time (US & Canada)": "America/Chicago",
			"Eastern Time (US & Canada)": "America/New_York",
			"Mountain Time (US & Canada)": "America/Denver",
			"Pacific Time (US & Canada)": "America/Los_Angeles",
			Arizona: "America/Phoenix",
			Alaska: "America/Anchorage",
			Hawaii: "Pacific/Honolulu",
			UTC: "Etc/UTC",
		};
		const normalized = timezoneAliases[value] || value;
		try {
			new Intl.DateTimeFormat("en-US", { timeZone: normalized });
			return normalized;
		} catch (error) {
			return DEFAULT_TIMEZONE;
		}
	};

	const TIMEZONE = resolveTimeZone(timezoneConfig);
	const formatterCache = new Map();

	const getFormatter = (timezone = TIMEZONE) => {
		const resolvedTimeZone = resolveTimeZone(timezone);
		if (!formatterCache.has(resolvedTimeZone)) {
			formatterCache.set(
				resolvedTimeZone,
				new Intl.DateTimeFormat("en-US", {
					timeZone: resolvedTimeZone,
					year: "numeric",
					month: "numeric",
					day: "numeric",
					hour: "numeric",
					minute: "numeric",
					second: "numeric",
					hour12: false,
				}),
			);
		}
		return formatterCache.get(resolvedTimeZone);
	};

	const pad = (value) => String(value).padStart(2, "0");
	const toDateKey = (year, month, day) => `${year}-${pad(month)}-${pad(day)}`;

	const parseDateKey = (value) => {
		if (!value) return null;
		const text = String(value).trim();
		if (!text) return null;

		const isoMatch = text.match(/^(\d{4}-\d{2}-\d{2})/);
		if (isoMatch) return isoMatch[1];

		const usMatch = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
		if (usMatch) {
			const month = Number(usMatch[1]);
			const day = Number(usMatch[2]);
			const year = Number(usMatch[3]);
			return toDateKey(year, month, day);
		}

		const parsed = new Date(text);
		if (Number.isNaN(parsed.getTime())) return null;
		return toDateKey(
			parsed.getFullYear(),
			parsed.getMonth() + 1,
			parsed.getDate(),
		);
	};

	const dateKeyToLocalDate = (dateKey) => {
		const parsed = parseDateKey(dateKey);
		if (!parsed) return null;
		const [year, month, day] = parsed.split("-").map(Number);
		return new Date(year, month - 1, day);
	};

	const addDaysToDateKey = (dateKey, days) => {
		const [year, month, day] = dateKey.split("-").map(Number);
		const date = new Date(Date.UTC(year, month - 1, day));
		date.setUTCDate(date.getUTCDate() + days);
		return toDateKey(
			date.getUTCFullYear(),
			date.getUTCMonth() + 1,
			date.getUTCDate(),
		);
	};

	const getDayOfWeek = (dateKey) => {
		const [year, month, day] = dateKey.split("-").map(Number);
		return new Date(Date.UTC(year, month - 1, day)).getUTCDay();
	};

	const getChicagoParts = (date = new Date(), timezone = TIMEZONE) => {
		const formatter = getFormatter(timezone);
		const parts = Object.fromEntries(
			formatter.formatToParts(date).map((part) => [part.type, part.value]),
		);
		const year = Number(parts.year);
		const month = Number(parts.month);
		const day = Number(parts.day);
		const hour = Number(parts.hour);
		const minute = Number(parts.minute);
		const second = Number(parts.second);
		return {
			year,
			month,
			day,
			hour,
			minute,
			second,
			dateKey: toDateKey(year, month, day),
		};
	};

	const normalizeTimeFloor = (now = new Date(), options = {}) => {
		const cutoffHour = options.cutoffHour ?? DEFAULT_CUTOFF_HOUR;
		const leadMinutes = options.leadMinutes ?? 0;
		const timezone = options.timezone ?? TIMEZONE;
		const shiftedNow = new Date(now.getTime() + leadMinutes * 60 * 1000);
		const chicago = getChicagoParts(shiftedNow, timezone);
		const minutesSinceMidnight = chicago.hour * 60 + chicago.minute;
		const cutoffMinutes = cutoffHour * 60;
		const isPreviousDay = minutesSinceMidnight < cutoffMinutes;
		const normalizedDateKey = isPreviousDay
			? addDaysToDateKey(chicago.dateKey, -1)
			: chicago.dateKey;
		return {
			currentChicagoDateKey: chicago.dateKey,
			normalizedDateKey,
			minutesSinceMidnight,
			cutoffMinutes,
			leadMinutes,
			timezone,
		};
	};

	const normalizeHolidayKeys = (holidays = []) =>
		holidays.map((holiday) => parseDateKey(holiday)).filter(Boolean);

	const isHoliday = (dateKey, holidaySet) => holidaySet.has(dateKey);

	const isBusinessDay = (dateKey, holidaySet) => {
		const dayOfWeek = getDayOfWeek(dateKey);
		if (dayOfWeek === 0 || dayOfWeek === 6) return false;
		if (isHoliday(dateKey, holidaySet)) return false;
		return true;
	};

	const getBusinessDayReason = (dateKey, holidaySet) => {
		const dayOfWeek = getDayOfWeek(dateKey);
		if (dayOfWeek === 0) return "Sunday";
		if (dayOfWeek === 6) return "Saturday";
		if (isHoliday(dateKey, holidaySet)) return "Holiday";
		return "Business day";
	};

	const addBusinessDaysWithTrace = (startDateKey, days, holidaySet) => {
		let cursor = startDateKey;
		let added = 0;
		const trace = [];

		while (added < days) {
			cursor = addDaysToDateKey(cursor, 1);
			const counted = isBusinessDay(cursor, holidaySet);
			const reason = getBusinessDayReason(cursor, holidaySet);
			if (counted) added += 1;
			trace.push({
				dateKey: cursor,
				reason,
				counted,
				addedSoFar: added,
			});
		}

		return { dateKey: cursor, trace };
	};

	const findNextDayOfWeek = (startDateKey, targetDayOfWeek) => {
		let cursor = startDateKey;
		while (getDayOfWeek(cursor) !== targetDayOfWeek) {
			cursor = addDaysToDateKey(cursor, 1);
		}
		return cursor;
	};

	const isSaturdayMondayWindow = (now = new Date(), options = {}) => {
		const cutoffHour = options.cutoffHour ?? DEFAULT_CUTOFF_HOUR;
		const leadMinutes = options.leadMinutes ?? 0;
		const timezone = options.timezone ?? TIMEZONE;
		const normalized = normalizeTimeFloor(now, {
			cutoffHour,
			leadMinutes,
			timezone,
		});
		return getDayOfWeek(normalized.normalizedDateKey) === 3;
	};

	const getSaturdayMondayKeys = (now = new Date(), options = {}) => {
		if (!isSaturdayMondayWindow(now, options)) return [];
		const leadMinutes = options.leadMinutes ?? 0;
		const timezone = options.timezone ?? TIMEZONE;
		const shiftedNow = new Date(now.getTime() + leadMinutes * 60 * 1000);
		const chicago = getChicagoParts(shiftedNow, timezone);
		const saturdayKey = findNextDayOfWeek(chicago.dateKey, 6);
		const mondayKey = addDaysToDateKey(saturdayKey, 2);
		return [saturdayKey, mondayKey];
	};

	const getNextSaturdayMondayKeys = (now = new Date(), options = {}) => {
		const leadMinutes = options.leadMinutes ?? 0;
		const timezone = options.timezone ?? TIMEZONE;
		const shiftedNow = new Date(now.getTime() + leadMinutes * 60 * 1000);
		const chicago = getChicagoParts(shiftedNow, timezone);
		const saturdayKey = findNextDayOfWeek(chicago.dateKey, 6);
		const mondayKey = addDaysToDateKey(saturdayKey, 2);
		return [saturdayKey, mondayKey];
	};

	const computeDeliveryConstraints = (config = {}) => {
		const now = config.now ?? new Date();
		const cutoffHour = config.cutoffHour ?? DEFAULT_CUTOFF_HOUR;
		const leadMinutes = config.leadMinutes ?? 0;
		const timezone = config.timezone ?? TIMEZONE;
		const fulfillmentHolidayKeys = normalizeHolidayKeys(
			config.fulfillmentHolidays ?? [],
		);
		const fedexHolidayKeys = normalizeHolidayKeys(config.fedexHolidays ?? []);
		const fulfillmentHolidaySet = new Set(fulfillmentHolidayKeys);
		const fedexHolidaySet = new Set(fedexHolidayKeys);

		const normalized = normalizeTimeFloor(now, {
			cutoffHour,
			leadMinutes,
			timezone,
		});
		const fulfillmentResult = addBusinessDaysWithTrace(
			normalized.normalizedDateKey,
			1,
			fulfillmentHolidaySet,
		);
		const fedexResult = addBusinessDaysWithTrace(
			fulfillmentResult.dateKey,
			2,
			fedexHolidaySet,
		);
		const specialAllowedDateKeys = getSaturdayMondayKeys(now, {
			cutoffHour,
			leadMinutes,
			timezone,
		});
		const nextSaturdayMondayKeys = getNextSaturdayMondayKeys(now, {
			leadMinutes,
			timezone,
		});

		return {
			timezone: resolveTimeZone(timezone),
			now,
			currentChicagoDateKey: normalized.currentChicagoDateKey,
			normalizedDateKey: normalized.normalizedDateKey,
			minSelectableDateKey: fedexResult.dateKey,
			specialAllowedDateKeys,
			nextSaturdayMondayKeys,
			fulfillmentHolidayKeys,
			fedexHolidayKeys,
			fulfillmentTrace: fulfillmentResult.trace,
			fedexTrace: fedexResult.trace,
		};
	};

	const isDateSelectable = (dateKey, constraints) => {
		const normalizedDateKey = parseDateKey(dateKey);
		if (!normalizedDateKey || !constraints) return false;
		const dayOfWeek = getDayOfWeek(normalizedDateKey);
		if (dayOfWeek === 0) return false;

		const specialAllowedDateKeys = constraints.specialAllowedDateKeys ?? [];
		const nextSaturdayMondayKeys = constraints.nextSaturdayMondayKeys ?? [];

		const fulfillmentSet = new Set(constraints.fulfillmentHolidayKeys ?? []);
		const fedexSet = new Set(constraints.fedexHolidayKeys ?? []);
		if (
			fulfillmentSet.has(normalizedDateKey) ||
			fedexSet.has(normalizedDateKey)
		) {
			return false;
		}

		const isSaturdayOrMonday = dayOfWeek === 6 || dayOfWeek === 1;
		if (
			isSaturdayOrMonday &&
			nextSaturdayMondayKeys.includes(normalizedDateKey)
		) {
			if (specialAllowedDateKeys.includes(normalizedDateKey)) {
				return true;
			}
			const minDateKey = parseDateKey(constraints.minSelectableDateKey);
			const minDateIsUpcomingMonday =
				minDateKey &&
				getDayOfWeek(minDateKey) === 1 &&
				nextSaturdayMondayKeys.includes(minDateKey);
			if (minDateIsUpcomingMonday) {
				return false;
			}
		}

		if (normalizedDateKey >= constraints.minSelectableDateKey) return true;
		return specialAllowedDateKeys.includes(normalizedDateKey);
	};

	const toChicagoIsoAtSix = (dateKey, timezone = TIMEZONE) => {
		const [year, month, day] = dateKey.split("-").map(Number);
		const searchStart =
			Date.UTC(year, month - 1, day, 0, 0, 0) - 18 * 60 * 60 * 1000;
		const searchEnd = searchStart + 48 * 60 * 60 * 1000;
		for (
			let utcMillis = searchStart;
			utcMillis <= searchEnd;
			utcMillis += 15 * 60 * 1000
		) {
			const parts = getChicagoParts(new Date(utcMillis), timezone);
			if (
				parts.year === year &&
				parts.month === month &&
				parts.day === day &&
				parts.hour === 6 &&
				parts.minute === 0
			) {
				return new Date(utcMillis).toISOString();
			}
		}
		return new Date(Date.UTC(year, month - 1, day, 12, 0, 0)).toISOString();
	};

	const computeRequestedDateCutoff = (now = new Date()) => {
		const timezone = TIMEZONE;
		const normalized = normalizeTimeFloor(now, {
			cutoffHour: DEFAULT_CUTOFF_HOUR,
			leadMinutes: 0,
			timezone,
		});
		let targetKey = normalized.normalizedDateKey;
		do {
			targetKey = addDaysToDateKey(targetKey, 1);
		} while ([0, 6].includes(getDayOfWeek(targetKey)));
		return toChicagoIsoAtSix(targetKey, timezone);
	};

	global.RequestedDateUtils = {
		TIMEZONE,
		parseDateKey,
		dateKeyToLocalDate,
		addDaysToDateKey,
		getDayOfWeek,
		normalizeTimeFloor,
		computeDeliveryConstraints,
		isDateSelectable,
		computeRequestedDateCutoff,
	};
})(window);
